<template>
  <div>
    <div ref="chart" class="container-chart"></div>
    <div ref="created"></div>
    <div v-if="noData" ref="noData">
      <p style="text-align: center">NO DATA</p>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { tip as d3tip } from 'd3-v6-tip';
import { hierarchy, tree } from 'd3-hierarchy';
import { getTooltipContent, ResizeObserver } from '../chart-services';

export default {
  name: 'Treemap',
  props: {
    width: { type: String },
    height: { type: String, default: '500' },
    autoWidth: { type: Boolean, default: false },
    autoHeight: { type: Boolean, default: false },
    data: { type: String },
    dataApi: { type: String },
    filters: {
      type: String //, default: '{"node-type":"tree_data","chart-type":"sankey","filters":{"field_geo":"121","field_year":"2015","field_indicator":"396","moderation_state":["published"]}}'
    },
    apiKey: {
      type: String //, default: '298fde64234827a09f6dfccab48c17f2'
    },
    showDate: { type: Boolean, default: true },
    showLabel: { type: Boolean, default: true },
    animationDuration: { type: Number },
    toggleNode: { type: Boolean, default: true },
    autoNodeGap: { type: Boolean, default: true },
    nodeGap: { type: Number, default: 140 }
  },
  data() {
    return {
      jsonData: null,
      nodeDataMapping: {},
      rootNode: {},
      margin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      selected: null,
      color: {},
      root: null,
      adjustMap: {},
      n_samples: null,
      line: d3.line().curve(d3.curveBasis), //.interpolate("basis"),
      i: 0,
      max_node_width: 110,
      defaultLayerHeight: 140,
      layer_height: 140, // y difference between nodes (adjustable)
      rectWidth: 10, // global height of every node (adjustable to preference)
      svg: null,
      gWrapper: null,
      treemap: null, //d3.tree().nodeSize([170, 170]), //.nodeSize([this.h, this.w]),
      stroke_color: 'rgba(227, 227, 227, 0.9)',
      fill_color: '#636363',
      tip: d3tip().attr('class', 'custom-tooltip d3-tip').html((EVENT, d) => d),
      label_font_size: 13,
      tooltip_font_size: 14,
      label_font_style: 'Roboto,sans-serif',
      noData: false,
      width_: null,
      height_: null,
      tooltip: '',
      isNumeric: false,
      resizeObserver$: null
    };
  },
  watch: {
    data: function() {
      this.fetchData();
    },
    filters: function (newVal, oldVal) {
      this.fetchData();
    },
    showLabel: function () {
      this.fetchData();
    }
  },
  mounted() {
    this.initialize();
  },
  destroyed: function () {
    if (this.resizeObserver$ !== null) ResizeObserver.unobserve(this.resizeObserver$);
  },
  methods: {
    getDepth: function (obj) {
      const self = this;
      let depth = 0;
      if (obj.children) {
        obj.children.forEach(function (d) {
          const tmpDepth = self.getDepth(d);
          if (tmpDepth > depth) {
            depth = tmpDepth;
          }
        });
      }
      return 1 + depth;
    },
    numberWithCommas: function (x) {
      return x.toLocaleString();
    },
    getTextWidth: function (text, fontSize, fontStyle) {
      const canvas = document.createElement("canvas"),
        context = canvas.getContext("2d");
      context.font = fontSize + "px " + fontStyle;
      return context.measureText(text).width;
    },
    setNoDataText: function () {
      this.noData = true;
      this.$refs.chart.style.display = "none";
      this.$refs.created.style.display = "none";
    },
    hideNoDataText: function () {
      this.noData = false;
      this.$refs.chart.style.display = "block";
      this.$refs.created.style.display = "block";
    },
    calculateWidth(width, depth) {
      this.width_ = width;
      const availableSpace = width - (this.rectWidth * depth) - this.margin.left - this.margin.right;
      const tempHt = availableSpace / depth;
      if (depth > 5 && tempHt < this.defaultLayerHeight) this.label_font_size = 11;
      // this.layer_height = tempHt > this.layer_height ? this.layer_height : tempHt;
      if (this.autoNodeGap === true) {
        this.layer_height = tempHt < this.layer_height ? this.defaultLayerHeight: tempHt;
      }
    },
    fetchData() {
      const dataResponse$ = this.dataApi !== undefined
        ? d3.json(this.dataApi, {
          method: 'POST',
          body: this.filters,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
            'api-key': this.apiKey
          },
          credentials: 'omit'
        })
        : new Promise(resolve => resolve(JSON.parse(this.data)));
      dataResponse$.then((json) => {
        if (json.data === undefined ||
          json.data.children === undefined ||
          json.data.children.length === 0) {
          this.setNoDataText();
          return;
        }
        this.hideNoDataText();
        this.tip.hide();
        // console.log('json data');
        // console.log(json.data);
        this.tooltip = json.data.tooltip;
        this.isNumeric = json.data.is_numeric;
        this.defaultLayerHeight = this.nodeGap;
        this.layer_height = this.nodeGap;
        const depth = this.getDepth(json.data.children[0]);
        // if(self.width) this.calculateWidth(depth);
        // else
        //   self.width_ =
        //     (depth - 1) * this.rectWidth +
        //     depth * this.layer_height +
        //     this.margin.left +
        //     this.margin.right;
        if (this.autoWidth === true) {
          this.calculateWidth(this.$el.offsetWidth, depth);
        } else if (this.width) {
          this.calculateWidth(this.width, depth);
        } else {
          this.width_ =
            depth * this.rectWidth + // (depth - 1) * this.rectWidth +
            depth * this.layer_height +
            this.margin.left +
            this.margin.right;
        }
        this.height_ = this.autoHeight === true
          ? this.$el.parentElement.offsetHeight
          : this.height;
        d3.select(this.$refs.chart)
          .select('svg')
          .style('overflow', 'visible')
          .attr('height', parseFloat(this.height_));
        this.treemap = d3.tree().size([this.height_, this.width_]);
        if (this.showDate === true) {
          d3.select(this.$refs.created).attr('style', `width:${this.width_}px;text-align:center;font-style:italic;font-size:12px;margin-top:-50px;`);
          d3.select(this.$refs.created).html(`Data generated date: ${json.data.created}`);
        }
        d3.select(this.$refs.chart).select('svg').attr('width', this.width_);
        this.loadDataset(json.data);
        const tipDirection = this.tooltip.direction !== undefined
          ? this.tooltip.direction
          : 'n';
        this.tip.direction(tipDirection);
        this.svg.call(this.tip);
      });
    },
    loadDataset(json) {
      this.root = hierarchy(json, function (d) {
        return d.children;
      });
      this.root.x0 = this.width_ / 2;
      this.root.y0 = this.height_ / 2;
      this.n_samples = json.children[0].samples; //total number of samples in tree
      // recursively toggles all nodes in tree
      // function toggleAll(d) {
      //   if (d && d.children) {
      //     d.children.forEach(toggleAll);
      //     self.toggle(d);
      //   }
      // }
      function scaleAll(d) {
        this.scale(d);
        if (d && d.children) {
          d.children.forEach(d2 => scaleAll.call(this, d2));
        }
      }
      scaleAll.call(this, this.root);
      // Initialize the display to show a few nodes.
      // turns off all not in the first layer
      // self.root.children.forEach(toggleAll);
      // draws
      this.update(this.root);
    },
    update(source) {
      function getPathColor (d, fallBackVal) {
        if (d.data.path_color) return d.data.path_color;
        return fallBackVal;
      }
      function getLabel (d) {
        if (typeof d === 'object') return d.value
        return d
      }
      const self = this;
      const duration = this.animationDuration !== undefined
        ? this.animationDuration
        : 0; //d3.event && d3.event.altKey ? 5000 : 500;
      const treeData = this.treemap(this.root);
      // Compute the new tree layout.
      // const rootNode = treeData.descendants()[0];
      const nodes = treeData.descendants().slice(1).map(node => {
        return { key: `${getLabel(node.data.label)}_${node.data.samples}`, ...node };
      });
      // const links = treeData.descendants().slice(rootNode.children.length + 1);
      this.nodeDataMapping = {};
      nodes.forEach(node => this.nodeDataMapping[node.key] = JSON.parse(JSON.stringify(node.data)))
      // Normalize for fixed-depth.
      nodes.forEach((d) => { d.y = d.depth * this.layer_height; });
      const rectClass = {
        greenBox: 'rect-green-box',
        redBox: 'rect-red-box',
        tooltipBox: 'rect-tooltip-box'
      };

      // Update the nodes…
      const nodeUpdate = this.gWrapper.selectAll('.node').data(nodes, function (d) {
        return d.key; // d.id || (d.id = ++self.i);
      });

      // Enter any new nodes at the parent's previous position.
      const nodeEnter = nodeUpdate
        .enter()
          .append('g')
          .attr('class', 'node')
          // .attr('transform', (d) => {
          //   return `translate(${source.y}, ${source.x})`;
          // });
      if (this.toggleNode === true) {
        nodeEnter.on('click', (event, d) => {
          this.tip.hide();
          this.toggle(d);
          this.update(d);
        });
      }

      /* green box */
      nodeEnter
        .selectAll(`.${rectClass.greenBox}`)
        .data(d => [d])
        .join(
          enter => {
            enter
              .append('rect')
              .attr('class', rectClass.greenBox)
              // .attr('y', (d) => {
              //   return -d.data.value[0] / 2;
              // })
              .attr('y', (d) => {
                return (d.data.value[0] / 2);
              })
              .attr('width', this.rectWidth)
              .attr('height', 0)
              .transition()
              .duration(duration)
              .attr('y', (d) => {
                return -(d.data.value[0] / 2);
              })
              .attr('height', (d) => {
                if (isNaN(d.data.value[0])) return null;
                return d.data.value[0];
              })
              .style('fill', (d) => {
                return this.nodeDataMapping[d.key].color;
              });
          },
          update => {
            update 
              .transition()
              .duration(duration)
              .attr('y', (d) => {
                return -d.data.value[0] / 2;
              })
              .attr('width', this.rectWidth)
              .attr('height', (d) => {
                if (isNaN(d.data.value[0])) return null;
                return d.data.value[0];
              })
              .style('fill', (d) => {
                return this.nodeDataMapping[d.key].color;
              });
          },
          exit => {
            exit.remove();
          }
        );

      /* red box */
      nodeEnter
        .append("rect")
        .attr('class', rectClass.redBox)
        .attr("y", function (d) {
          if (d.data.value[0]) {
            return d.data.value[0] / 2;
          } else {
            return -d.data.value[1] / 2;
          } //position depends on size of green box
        })
        .attr("width", function (d) {
          if (isNaN(d.data.value[1])) return;
          return d.data.value[1];
        })
        .attr("height", self.rectWidth)
        .style('fill', (d) => {
          return this.nodeDataMapping[d.key].color;
        });

      // invisible box surrounding both
      // used for mouseover effects
      nodeEnter
        .append('rect')
        .attr('class', rectClass.tooltipBox)
        .attr('y', (d) => {
          return -d.data.value[0] / 2;
        })
        .attr('width', this.rectWidth)
        .attr('height', (d) => {
          if (isNaN(d.data.value[0])) return d.data.value[1];
          if (isNaN(d.data.value[1])) return d.data.value[0];
          return d.data.value[0] + d.data.value[1];
        })
        .style('fill', 'pink')
        .style('fill-opacity', 0.1)
        .on('mouseover', function (event, d) {
          d3.select(this).style('stroke', 'white');
          // console.log(self.tooltip.value);
          // console.log(self.nodeDataMapping[d.key]);
          // console.log(d);
          // console.log(JSON.stringify(d.data));
          self.tip.show(
            event,
            getTooltipContent.call(self, self.tooltip.value, self.nodeDataMapping[d.key])
          );
        })
        .on('mouseout', function (d) {
          d3.select(this).style('stroke', null);
          self.tip.hide();
        });
        // .append("title")
        // .text(function (d) {
        //   return Math.round(
        //     ((d.data.value[0] + d.data.value[1]) * self.n_samples) /
        //       self.max_node_width
        //   );
        // });

      if (this.tooltip.showItem !== undefined) {
        const delay = 0;
        setTimeout(() => {
          const showItem = this.tooltip.showItem;
          this.gWrapper.selectAll(`.${rectClass.tooltipBox}`)
            .call(rects => {
              rects.each(function (data) {
                const val = showItem.attr === 'label'
                  ? getLabel(data.data[showItem.attr]) : data.data[showItem.attr]
                if (val !== showItem.val) return;
                this.dispatchEvent(new MouseEvent('mouseover'));
                return false; // We can only show one tooltip at a time
              });
            });
        }, delay); // Waiting for rendering animation
      }

      // Creating red green and green path objects
      // Paths will connect upward from child to parent
      // paths are broken into 2 classes (one for each category)
      // this is because the math is a little different depending on path color

      function hoverLink () {
        d3.select(this).attr('stroke-opacity', '.7');
      }
      function unhoverLink () {
        d3.select(this).attr('stroke-opacity', '1');
      }

      nodeEnter
        .append('path')
        .attr('class', 'leftlink')
        .style('stroke', d => getPathColor(d, this.stroke_color))
        // .style("stroke-width", function (d) {
        //   if (d.parent.parent == null) return 0;
        //   return d.data.value[0];
        // })
        .attr('transform', function (d) {
          return 'scale(0.1, 0.1)';
        }) // path starts tiny
        .on('mouseover', function () {
          hoverLink.call(this);
        })
        .on('mouseout', function () {
          unhoverLink.call(this);
        })
        .transition()
        .duration(duration)
        .attr('transform', function (d) {
          return 'scale(1, 1)';
        }); // path ends large
      // .append("title")
      // .text(function (d) {
      //   return Math.round(
      //     (d.data.value[0] * self.n_samples) / self.max_node_width
      //   );
      // }); //displays tooltip on mouseover

      nodeEnter
        .append('path')
        .attr('class', 'rightlink')
        .style('stroke', d => getPathColor(d, this.stroke_color))
        // .style("stroke-width", function (d) {
        //   if (d.parent.parent == null) return 0;
        //   return d.data.value[0];
        // })
        .attr('transform', function (d) {
          return 'scale(0.1, 0.1)';
        }) // path starts tiny
        .on('mouseover', function () {
          hoverLink.call(this);
        })
        .on("mouseout", function () {
          unhoverLink.call(this);
        })
        .transition()
        .duration(duration)
        .attr('transform', function (d) {
          return 'scale(1, 1)';
        }); // path ends large
      // .append("title")
      // .text(function (d) {
      //   return Math.round(
      //     (d.data.value[1] * self.n_samples) / self.max_node_width
      //   );
      // }); //displays tooltip on mouseover

      /* Update node color */
      nodeUpdate
        .selectAll(`.${rectClass.greenBox}`)
        .style('fill', (d) => {
          return this.nodeDataMapping[d.key].color;
        });
      nodeUpdate
        .selectAll(`.${rectClass.redBox}`)
        .style('fill', (d) => {
          return this.nodeDataMapping[d.key].color;
        });
      /* /Update node color */
      /* Update path color */
      // nodeUpdate
      //   .selectAll('class', 'leftlink')
      //   .transition()
      //   .duration(duration)
      //   .style('stroke', d => getPathColor(d, this.stroke_color));
      // nodeUpdate
      //   .selectAll('class', 'rightlink')
      //   .transition()
      //   .duration(duration)
      //   .style('stroke', d => getPathColor(d, this.stroke_color));
      /* /Update path color */

      // Transition nodes to their new position.
      const nodeAll = nodeEnter.merge(nodeUpdate);
      nodeAll
        .attr('transform', function (d) {
          return `translate(${d.y}, ${d.x})`;
        });
      
      // handles tricky math to create path from child node to parent
      // each path is composed of 4 points (0/3 1/3 2/3 3/3) up the path
      //.leftlink = green path, .rightlink = red path
      const layerHeight1in3 = this.layer_height / 3;
      const layerHeight2Times1in3 = (-2 * this.layer_height) / 3;
      const availableLayerHeight = this.layer_height - this.rectWidth;
      nodeAll
        .select('.leftlink')
        .style('stroke-width', function (d) {
          if (d.parent.parent == null) return 0;
          return d.data.value[0];
        })
        .attr('d', function (d) {
          // let sourcey = 0;
          // let p = d.parent;
          let points = [];
          if (d.parent) {
            if (isNaN(d.parent.data.value[0]) || isNaN(d.data.value[0])) return;
            //left node of green branch
            if (d.parent.x > d.x) {
              points = [
                [0, 0],
                [-layerHeight1in3, 0],
                [
                  layerHeight2Times1in3,
                  d.parent.x -
                    d.x +
                    d.data.value[0] / 2 -
                    d.parent.data.value[0] / 2,
                ],
                [
                  -availableLayerHeight,
                  d.parent.x -
                    d.x +
                    d.data.value[0] / 2 -
                    d.parent.data.value[0] / 2,
                ],
              ];
            }
            //right node of green branch
            else {
              points = [
                [0, 0],
                [-layerHeight1in3, 0],
                [
                  layerHeight2Times1in3,
                  d.parent.x -
                    d.x +
                    (d.parent.data.value[0] - d.data.value[0]) / 2,
                ],
                [
                  -availableLayerHeight,
                  d.parent.x -
                    d.x +
                    (d.parent.data.value[0] - d.data.value[0]) / 2,
                ],
              ];
            }
          } else {
            points = [ [0, 0], [0, 0] ]; //no parent, no path
          }
          return self.line(points);
        })
        .style('stroke', d => getPathColor(d, this.stroke_color));

      // for the red path, the height of the 2 middle points are offset
      // this is done to avoid overlap with the green path
      // offset is 1/6 of layer height currently
      // height of the middle two points can be adjusted to fit preferences

      nodeAll
        .select('.rightlink')
        .style('stroke-width', function (d) {
          if (d.parent.parent == null) return 0;
          return d.data.value[1] * 1.0;
        })
        .attr('d', function (d) {
          // let targety = self.layer_height;
          // let targetx = self.adjustMap[d.data.label];
          // let sourcey = 0;
          const p = d.parent;
          let points;
          if (d.parent) {
            if (
              isNaN(d.parent.data.value[0]) ||
              isNaN(d.parent.data.value[1]) ||
              isNaN(d.data.value[0]) ||
              isNaN(d.data.value[1])
            )
              return;
            //red path left node
            if (d.parent.x > d.x) {
              points = [
                [0, d.data.value[0] / 2 + d.data.value[1] / 2],
                [
                  -((1 / 3) * self.layer_height - (1 / 6) * self.layer_height),
                  d.data.value[0] / 2 + d.data.value[1] / 2,
                ],
                [
                  (-2 / 3) * self.layer_height,
                  d.parent.x -
                    d.x +
                    d.parent.data.value[0] / 2 +
                    d.parent.data.value[1] / 2 -
                    (d.parent.data.value[1] - d.data.value[1]) / 2,
                ],
                [
                  -(self.layer_height - self.rectWidth),
                  d.parent.x -
                    d.x +
                    d.parent.data.value[0] / 2 +
                    d.parent.data.value[1] / 2 -
                    (d.parent.data.value[1] - d.data.value[1]) / 2,
                ],
              ];
            }
            //red path right node
            else {
              points = [
                [0, d.data.value[0] / 2 + d.data.value[1] / 2],
                [
                  -((1 / 3) * self.layer_height),
                  d.data.value[0] / 2 + d.data.value[1] / 2,
                ],
                [
                  -((2 / 3) * self.layer_height + self.layer_height / 6),
                  d.parent.x -
                    d.x +
                    d.parent.data.value[0] / 2 +
                    d.parent.data.value[1] / 2 +
                    (d.parent.data.value[1] - d.data.value[1]) / 2,
                ],
                [
                  -(self.layer_height - self.rectWidth),
                  d.parent.x -
                    d.x +
                    d.parent.data.value[0] / 2 +
                    d.parent.data.value[1] / 2 +
                    (d.parent.data.value[1] - d.data.value[1]) / 2,
                ],
              ];
            }
          } else {
            points = [
              [0, 0],
              [0, 0],
            ]; //no parent, no path
          }
          return self.line(points);
        })
        .style('stroke', d => getPathColor(d, this.stroke_color));

      const labelClassName = 'label';
      nodeEnter
        .append('text')
        .attr('class', labelClassName)
        // .attr("x", function (d) {
        //   return - ( self.getTextWidth(
        //         d.data.label,
        //         self.label_font_size,
        //         self.label_font_style
        //       ) + self.rectWidth );
        // }) //text is outside tree
        .attr('y', (d) => {
          return -(d.data.samples / 2);
        }) // 10 is for approx height of the line
        .attr('data-per', (d) => {
          return this.showLabel === true ? d.data.percentage : '';
        })
        // .style('fill', 'black')
        .style('fill', d => {
          if (d.data.label.style !== undefined &&
            d.data.label.style.color !== undefined) {
            return d.data.label.style.color;
          }
          return 'black';
        })
        .style('font-weight', d => {
          if (d.data.label.style !== undefined &&
            d.data.label.style.fontWeight !== undefined) {
            return d.data.label.style.fontWeight;
          }
          return null;
        })
        .text((d) => {
          if (this.showLabel === false) return '';
          return getLabel(d.data.label);
        })
        .call(d => {
          if (this.showLabel === false) return;
          this.wrapText(d, 100);
        });
      nodeAll
        .select(`.${labelClassName}`)
        .attr('y', (d) => {
          return -(d.data.samples / 2);
        }) // 10 is for approx height of the line
        .attr('data-per', (d) => {
          return this.showLabel === true ? d.data.percentage : '';
        })
        .style('fill', d => {
          if (d.data.label.style !== undefined &&
            d.data.label.style.color !== undefined) {
            return d.data.label.style.color;
          }
          return 'black';
        })
        .style('font-weight', d => {
          if (d.data.label.style !== undefined &&
            d.data.label.style.fontWeight !== undefined) {
            return d.data.label.style.fontWeight;
          }
          return null;
        })
        .text((d) => {
          if (this.showLabel === false) return '';
          return getLabel(d.data.label);
        })
        .call(d => {
          if (this.showLabel === false) return;
          this.wrapText(d, 100);
        });

      // Transition exiting nodes to the parent's new position.
      const nodeExit = nodeUpdate.exit();
        // .attr('transform', function (d) {
        //   return `translate(${source.y}, ${source.x})`;
        // });
      nodeExit
        .select(`.${rectClass.greenBox}`)
        .transition()
        .duration(duration)
        .attr('y', (d) => {
          return (d.data.value[0] / 2);
        })
        .attr('height', 0);
      //makes path smaller on exit
      nodeExit
        .select('.leftlink')
        .attr('transform', function (d) {
          return 'scale(0, 0)';
        });
      nodeExit
        .select('.rightlink')
        .attr('transform', function (d) {
          return 'scale(0, 0)';
        });
      nodeExit.select('text').style('fill-opacity', 1e-6);
      // // Update the links
      // const link = self.vis[0].selectAll("path.link").data(links, function (d) {
      //   return d.id;
      //   // return d.target.id;
      // });
      nodeExit.remove();

      // Stash the old positions for transition.
      nodes.forEach(function (d) {
        d.x0 = d.x;
        d.y0 = d.y;
      });

      if (this.autoWidth === true && this.resizeObserver$ === null) {
        this.resizeObserver$ = ResizeObserver.observe(this.$el, () => {
          this.fetchData();
        }, { onlyWidth: true, skipFirst: true });
      }
    },
    toggle(d) {
      if (d.children) {
        d._children = d.children;
        d.children = null;
      } else {
        d.children = d._children;
        d._children = null;
      }
    },
    scale(d) {
      d.data.original_samples = d.data.samples;
      d.data.samples /= this.n_samples / this.max_node_width;
      d.data.value[0] /= this.n_samples / this.max_node_width;
      d.data.value[1] /= this.n_samples / this.max_node_width;
    },
    initialize() {
      if (this.dataApi === undefined && this.data === undefined) {
        this.setNoDataText();
        return;
      }
      this.svg = d3
        .select(this.$refs.chart)
        .append('svg')
        .style('fill', 'transparent')
      this.gWrapper = this.svg
        .append('g')
        .attr('class', 'wrapper-g')
        .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
      this.fetchData();
    },
    wrapText(d, width) {
      const self = this;
      d.each(function () {
        const text = d3.select(this);
        const words = text.text().split(/\s+/).reverse();
        let word = words.pop();
        let line = [];
        let lineNumber = 0;
        const lineHeight = 1.1;
        // const x = text.attr("x");
        const y = text.attr("y");
        const dy = 0;
        let tspan = text
          .text(null)
          .append("tspan")
          .style("font-size", self.label_font_size)
          // .style("font-family", self.label_font_style)
          // .style("fill", self.fill_color)
          .attr("y", y)
          .attr("dy", dy + "em");
        let maxWidth = 0;
        while (word) {
          line.push(word);
          tspan.text(line.join(" "));
          const txt = line.join(" ");

          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            const txt = line.join(" ");
            line = [word];
            tspan = text
              .append("tspan")
              .style("font-size", self.label_font_size)
              // .style("font-family", self.label_font_style)
              // .style("fill", self.fill_color)
              .attr("y", y)
              .attr("dy", ++lineNumber * lineHeight + dy + "em")
              .text(word);
            const w = self.getTextWidth(
              txt,
              self.label_font_size,
              self.label_font_style
            );
            maxWidth = maxWidth > w ? maxWidth : w;
          } else {
            const w = self.getTextWidth(
              txt,
              self.label_font_size,
              self.label_font_style
            );
            maxWidth = maxWidth > w ? maxWidth : w;
          }
          word = words.pop();
        }
        const per = text.attr("data-per") + (self.isNumeric ? '' : '%');
        text
          .append("tspan")
          .attr("class", "label-per")
          .attr("y", y)
          .attr("dy", ++lineNumber * lineHeight + dy + "em")
          .style("font-size", self.label_font_size + 2)
          // .style("font-family", self.label_font_style)
          // .style("fill", "#2F2F2F")
          .text(per);
        maxWidth += self.rectWidth / 2;
        text.attr("x", -maxWidth);
        text.selectAll("tspan").attr("x", -maxWidth);
      });
    },
  },
};
</script>

<style scoped>
.node rect {
  cursor: pointer;
  fill: #fff;
  stroke-width: 2px;
}
.node text {
  font-family: "Average Sans";
  z-index: 100;
  font-size: 13px;
}
.d3-tip {
  font-family: Arial, Helvetica, sans-serif;
  line-height: 1.4;
  padding: 16px 20px;
  pointer-events: none !important;
  color: #203d5d;
  box-shadow: 0 4px 20px 4px rgba(0, 20, 60, 0.1),
    0 4px 80px -8px rgba(0, 20, 60, 0.2);
  background-color: #fff;
  border-radius: 4px;
  text-align: left;
}
.d3-tip:after {
  box-sizing: border-box;
  display: inline;
  font-size: 10px;
  width: 100%;
  line-height: 1;
  color: #fff;
  position: absolute;
  pointer-events: none;
}
.d3-tip.n:after {
  content: "▼";
  margin: -1px 0 0 0;
  top: 100%;
  left: 0;
  text-align: center;
}
.d3-tip.e:after {
  content: "◀";
  margin: -4px 0 0 0;
  top: 50%;
  left: -8px;
}
.d3-tip.s:after {
  content: "▲";
  margin: 0 0 1px 0;
  top: -8px;
  left: 0;
  text-align: center;
}
.d3-tip.w:after {
  content: "▶";
  margin: -4px 0 0 -1px;
  top: 50%;
  left: 100%;
}
</style>
